<template>
  <form
    v-shortkey="shortcutKeys"
    @shortkey="toggleShortkey"
    @submit.prevent="save"
    class="d-flex flex-column"
  >
    <div class="container bg-white p-2" id="specimen-form">
      <CaseCytologyQuestions :autoFocus="false" v-model="caseQuestions" :caseId="caseId" />
      <text-input
        label="Clinical ICD"
        accessKey="i"
        maxLength="81"
        ref="clinicalICDCode"
        id="clinicalICDCode"
        v-model="specimen.clinicalICDCode"
        :forceUpperCase="ForceUpperCaseClinicalICD10"
      />
      <div class="container">
        <macro-enabled-editor
          :class="{ 'text-uppercase': upperCaseClinical }"
          name="clinical"
          label="Clinical"
          ref="clinical"
          accessKey="c"
          :expanded="true"
          id="clinical"
          v-model="specimen.clinical"
          @editorReady="handleAutoOpenEditor"
        />
      </div>
    </div>
    <div class="header-panel">
      <icon-button data-testid="expandbtn" type="button" icon="chevron-up" class="mr-4" />
      <h2>Tests</h2>
      <h4 class="ml-auto" v-show="caseDetails.caseNumber">{{ caseDetails.caseNumber }}</h4>
    </div>
    <CytologyCaseTests
      :accession="true"
      @shortkey="toggleShortkey"
      :caseIdentifier="caseId"
      :specimenIdentifier="specimenId"
      :hideTabs="true"
    />
    <button class="btn btn-primary input-btn ml-auto mb-2 mr-2" type="submit">Save</button>
  </form>
</template>

<script>
import MacroEnabledEditor from "@/components/common/MacroEnabledEditor.vue";
import { mapState, mapGetters } from "vuex";
import { getAltKeys, removeExtraDivs } from "@/modules/helpers";
import CaseCytologyQuestions from "../CaseCytologyQuestions.vue";
import { CasesApi, SpecimensApi } from "@/services";
import cytologyService, { CytologyEntities } from "@/services/cytology";
import CytologyCaseTests from "@/components/CytologyCaseTests.vue";
import IconButton from "@/components/common/IconButton.vue";

export default {
  name: "CytologyAccession",
  components: {
    MacroEnabledEditor,
    CaseCytologyQuestions,
    CytologyCaseTests,
    IconButton
  },
  props: [
    "caseDetails",
    "currentPrefix",
    "protocolId",
    "isMolecularTestingOnly",
    "isProtocolRequired"
  ],
  data() {
    return {
      options: [],
      selected: 0,
      specimen: {},
      isLoading: false,
      specimens: [],
      selectedSpecimenBlocks: null,

      caseQuestions: {},
      hopperId: null,
      isModalOpen: false,
      labQuestions: {},
      shortcutKeys: getAltKeys("abistu"),
      editing: {
        allowUpdating: true,
        allowDeleting: true,
        confirmDelete: true,
        mode: "cell",
        useIcons: true,
        startEditAction: "click"
      },
      testOptions: cytologyService.createSearch(CytologyEntities.CytMolecTests),
      molecularPanelOptions: cytologyService.createSearch(CytologyEntities.CytMolecPanels),
      hoppers: [],
      hasContentReadyHappened: false,
      caseNotes: "",
      isProtocolPopupOpen: false,
      hasLoadedSpecimens: false
    };
  },
  mounted() {
    SpecimensApi.getSpecimen(this.caseId).then(data => {
      if (data?.length) {
        this.specimens = data;
        this.specimen = this.specimens[0];
      }
    });
  },
  created() {
    this.molecularPanelOptions.load().then(data => {
      this.options = [
        ...this.options,
        ...data.map(e => ({ id: e.id, displayName: `${e.displayText} (P)`, type: "panel" }))
      ];
      return this.testOptions.load().then(data => {
        this.options = [
          ...this.options,
          ...data.map(e => ({ id: e.id, displayName: `${e.displayText} (T)`, type: "test" }))
        ];
      });
    });
  },
  watch: {
    "specimen.specimenOrder": {
      immediate: true,
      handler(nv) {
        if (typeof nv !== "string") {
          return;
        }
        this.specimen.specimenOrder = nv.toUpperCase();
      }
    }
  },
  computed: {
    ...mapState({
      currentUser: state => state.currentUser,
      labSettings: state => state.labSettings,
      currentLab: state => state.currentLab,
      sessionDetails: state => state.sessionsDetails,
      ForceUpperCaseClinicalICD10: state => state.labSettings.ForceUpperCaseClinicalICD10,
      ForceUpperCaseClinical: state => state.labSettings.ForceUpperCaseClinical,
      defaultLastProtocol: state => state.labSettings.CytologyDefaultLastProtocol
    }),
    ...mapGetters(["permissions"]),
    ...mapGetters("accessionStore", ["specimenNumbering"]),
    caseId() {
      return this?.caseDetails?.caseId;
    },
    specimenId() {
      if (this.specimen) {
        return this.specimen.id;
      }
      return null;
    },
    upperCaseClinical() {
      const { ForceUpperCaseClinical } = this.labSettings;
      if (ForceUpperCaseClinical) {
        return true;
      }
      return false;
    }
  },

  methods: {
    handleAutoOpenEditor() {
      this.$refs.clinical.expand();
    },
    async handleSaveQuestions() {
      try {
        this.isLoading = true;
        const items = [];
        for (const questionId in this.caseQuestions) {
          const answer = this.caseQuestions[questionId];

          const item = {
            cytQuestionId: parseInt(questionId)
          };
          if (!isNaN(answer)) {
            item.cytQuestionOptionId = parseInt(answer);
          } else {
            item.response = answer;
          }

          items.push(item);
        }

        await CasesApi.insertCytologyResponses(this.caseId, {
          id: this.caseId,
          items: items
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    toggleShortkey(event) {
      switch (event.srcKey) {
        case "i":
          this.editCell("site");
          break;
        case "s":
          this.save();
          break;
        default:
          break;
      }
    },

    async save() {
      try {
        this.isLoading = true;
        this.specimen.clinical = removeExtraDivs(this.specimen.clinical || "");
        if (this.ForceUpperCaseClinical) {
          this.specimen.clinical = this.specimen.clinical.toUpperCase();
        }
        if (!this.protocolId && this.isProtocolRequired) {
          window.alert(
            "The protocol is required for this prefix. Please select a protocol before saving."
          );
          return;
        }

        const updatedSpecimen = SpecimensApi.updateSpecimen([
          {
            ...this.specimen,
            caseId: this.caseId,
            protocolId: this.protocolId,
            cytMolecTestsOnly: this.isMolecularTestingOnly,
            duringAccessioning: true
          }
        ]);
        const [questionsResponse, specimensResponse] = await Promise.all([
          this.handleSaveQuestions(),
          updatedSpecimen
        ]);
        if (this.protocolId != null) {
          this.$store.commit("sessionDetails/setLastCytologyProtocol", this.protocolId);
        }
        if (questionsResponse) {
          window.notify("Questions saved successfully.", "success");
        }
        if (specimensResponse) {
          window.notify("Specimen saved successfully.", "success");
        }
        this.$emit("save");
      } catch (e) {
        console.log(e);
        window.notify("Error saving specimen.", "error");
      } finally {
        this.isLoading = false;
      }
    },
    toggleModal() {
      this.isModalOpen = !this.isModalOpen;
      if (!this.isModalOpen) {
        this.targetId = null;
      } else {
        this.$nextTick(() => {
          const selector = this.$refs.selector;
          if (selector) {
            selector.focus();
          }
        });
      }
    }
  }
};
</script>
<style lang="scss" scoped>
tr {
  cursor: pointer;
}
.currentSpecimen {
  background-color: #e6f5fc;
}
td {
  input,
  select {
    border: none;
    background: none;
    width: 100%;
  }
}
::v-deep .dx-datagrid .dx-link[title="Delete"] {
  color: $red;
}
.input-btn {
  height: 2.5rem;
}
.header-panel {
  background-color: $gray;
  display: flex;
  padding: 10px 5px;
  text-align: left;
}
</style>
